import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { DEFAULT_IMAGE, pageSeo, SITE_NAME, SITE_URL } from '../config/seoData';
import type { SeoConfig } from '../config/seoData';

function setMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function setCanonical(url: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);
}

function setSchema(config: SeoConfig, url: string, image: string) {
  const existingSchema = document.head.querySelector<HTMLScriptElement>('script[data-seo-schema="true"]');
  existingSchema?.remove();

  const schema = config.getSchema
    ? config.getSchema(url, SITE_URL, SITE_NAME)
    : {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${SITE_URL}/#organization`,
            name: SITE_NAME,
            url: `${SITE_URL}/`,
          },
          {
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            url: `${SITE_URL}/`,
            name: SITE_NAME,
            publisher: {
              '@id': `${SITE_URL}/#organization`,
            },
          },
          {
            '@type': 'WebPage',
            '@id': `${url}#webpage`,
            url,
            name: config.title,
            description: config.description,
            image,
            isPartOf: {
              '@id': `${SITE_URL}/#website`,
            },
          },
        ],
      };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-schema', 'true');
  script.textContent = JSON.stringify(schema, null, 2);
  document.head.appendChild(script);
}

export default function Seo() {
  const location = useLocation();

  useEffect(() => {
    const config = pageSeo[location.pathname] ?? pageSeo['/'];
    const url = `${SITE_URL}${config.path}`;
    const image = config.image ?? DEFAULT_IMAGE;

    document.title = config.title;
    setMeta('meta[name="description"]', { name: 'description', content: config.description });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: config.title });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: config.description });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: image });
    setCanonical(url);
    setSchema(config, url, image);
  }, [location.pathname]);

  return null;
}
